import { useDispatch } from 'react-redux';
import { useEffect, useCallback } from 'react';

import { isManagementRole } from 'src/permissions';
import { setActiveSubscription } from 'src/pages/setting/slice/settingSlice';
import { useGetSubscriptionListMutation } from 'src/pages/setting/components/billing/slice/billingApi';

import {
  getTenantId,
  getUserRole,
  getTenantName,
  hasBillingViewPermission,
} from './permission-utils';

import type { PermissionsMap } from './permission-utils';

export function useActiveSubscriptionEffect(
  loginApiData: unknown,
  permissions: PermissionsMap | null
) {
  const dispatch = useDispatch();
  const [getSubscriptionList] = useGetSubscriptionListMutation();

  const tenantId = getTenantId(loginApiData);
  const userRole = getUserRole(loginApiData);
  const hasBillingView = hasBillingViewPermission(permissions);
  const isManagement = isManagementRole(userRole);
  const canFetchBilling = hasBillingView || isManagement;

  const fetchSubscriptions = useCallback(async () => {
    if (
      !tenantId ||
      (userRole && userRole.toLowerCase() === 'user') ||
      !canFetchBilling
    ) {
      dispatch(setActiveSubscription(null));
      return;
    }

    try {
      const response = await getSubscriptionList({
        filters: [
          { fieldname: 'status', search: 'active' },
          { fieldname: 'tenantId', search: tenantId },
        ],
        globalSearch: '',
        page: 1,
        limit: 10,
      }).unwrap();

      if (response?.success && response?.data) {
        let subData: any = null;
        const resData = response.data;
        if (resData.subscription) {
          subData = resData.subscription;
        } else if (Array.isArray(resData) && resData.length > 0) {
          subData = resData[0]?.subscription || resData[0];
        } else if (Array.isArray(resData?.subscriptions) && resData.subscriptions.length > 0) {
          subData = resData.subscriptions[0]?.subscription || resData.subscriptions[0];
        } else if (resData?.plan || resData?.plan_id || resData?.subscriptionId || resData?.id) {
          subData = resData;
        }
        dispatch(setActiveSubscription(subData));
      } else {
        dispatch(setActiveSubscription(null));
      }
    } catch {
      dispatch(setActiveSubscription(null));
    }
  }, [dispatch, getSubscriptionList, tenantId, userRole, canFetchBilling]);

  useEffect(() => {
    const tenantName = getTenantName(loginApiData);

    if (
      !tenantId ||
      (userRole && userRole.toLowerCase() === 'user') ||
      !canFetchBilling
    ) {
      dispatch(setActiveSubscription(null));
      return;
    }
    if (!tenantName || tenantName === 'MASTER') return;

    fetchSubscriptions();
  }, [fetchSubscriptions, tenantId, loginApiData, dispatch, userRole, canFetchBilling]);
}
